import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  knowledgeService,
  CreateKnowledgeData,
  UpdateKnowledgeData,
} from '../services/knowledgeService';

const QUERY_KEY = ['knowledge'];

export function useKnowledgeList(params?: {
  domain?: string;
  activeOnly?: boolean;
}) {
  return useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => knowledgeService.list(params),
  });
}

export function useCreateKnowledge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKnowledgeData) => knowledgeService.create(data),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(`"${entry.title}" created`);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail || 'Failed to create knowledge entry'
      );
    },
  });
}

export function useUpdateKnowledge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKnowledgeData }) =>
      knowledgeService.update(id, data),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(`"${entry.title}" updated`);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail || 'Failed to update knowledge entry'
      );
    },
  });
}

export function useDeleteKnowledge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => knowledgeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Entry deleted');
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail || 'Failed to delete knowledge entry'
      );
    },
  });
}

export function useToggleKnowledgeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active
        ? knowledgeService.activate(id)
        : knowledgeService.deactivate(id),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(
        entry.active ? `"${entry.title}" activated` : `"${entry.title}" deactivated`
      );
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    },
  });
}
